import { partition } from 'lodash-es';

import type { Unit } from '@dynatrace-sdk/units';

import {
  CustomColorPalette,
  ColorPalette,
} from '../../core/types/color-palette.js';
import { THRESHOLD_TYPE } from '../types/default.js';
import { InternalSliceData } from '../types/internal/pie-chart.js';
import {
  ThresholdConfig,
  SliceData,
  GroupConfig,
} from '../types/pie-chart.config.js';
import { buildSlices } from './build-slices.js';

/**
 * Group the data depending on which threshold the user has selected
 * @param data - Data received from the dataSet
 * @param grouping - Received grouping config
 * @param colorPalette - Palette to apply to the slices.
 * @param unit - Unit of the data.
 */
export function buildGroupThreshold(
  // oxlint-disable-next-line typescript/no-deprecated
  data: SliceData[],
  grouping?: GroupConfig,
  colorPalette?: ColorPalette | CustomColorPalette,
  unit?: string | Unit,
): InternalSliceData[] {
  const internalSlicesData = buildSlices(data, colorPalette, unit);

  const threshold = grouping?.threshold;

  if (threshold?.type === THRESHOLD_TYPE.NUMBER_OF_SLICES) {
    return calculateNumberOfSlicesGrouping(
      internalSlicesData,
      threshold,
      grouping?.name,
    );
  }

  if (threshold?.type === THRESHOLD_TYPE.RELATIVE) {
    return calculateRelativeGrouping(
      internalSlicesData,
      threshold,
      grouping?.name,
    );
  }

  if (threshold?.type === THRESHOLD_TYPE.ABSOLUTE) {
    return calculateAbsoluteGrouping(
      internalSlicesData,
      threshold,
      grouping?.name,
    );
  }

  return internalSlicesData;
}

/**
 * Calculates the groups depending on the number of the slices.
 * @param data - Information received from the dataset already prepared with some metadata.
 * @param threshold - Indicates the way to group the slices.
 * @param groupingName - Name for the group.
 * @returns An array of an internal slices.
 */
function calculateNumberOfSlicesGrouping(
  data: InternalSliceData[],
  threshold: ThresholdConfig,
  groupingName?: string,
): InternalSliceData[] {
  const slicesWithValue = data.filter((slice) => slice.value);
  const slicesToMerge = slicesWithValue.slice(threshold.value - 1);
  const remainingSlices = slicesWithValue.slice(0, threshold.value - 1);

  if (threshold.value === 0) {
    return [...slicesToMerge, ...remainingSlices];
  }

  return generateGroup(slicesToMerge, remainingSlices, groupingName);
}

/**
 * Calculates the relative value using the threshold value and groups the slices.
 * @param data - Information received from the dataset already prepared with some metadata.
 * @param threshold - Indicates the way to group the slices.
 * @param groupingName - Name for the group.
 * @returns An array of an internal slices.
 */
function calculateRelativeGrouping(
  data: InternalSliceData[],
  threshold: ThresholdConfig,
  groupingName?: string,
): InternalSliceData[] {
  const slicesWithValue = data.filter((slice) => slice.value);

  const [slicesToMerge, remainingSlices] = partition(
    slicesWithValue,
    (slice) => slice.relativeValue < threshold.value,
  );

  return generateGroup(slicesToMerge, remainingSlices, groupingName);
}

/**
 * Calculates the absolute value using the threshold value and groups the slices.
 * @param data - Information received from the dataset already prepared with some metadata.
 * @param threshold - Indicates the way to group the slices.
 * @param groupingName - Name for the group.
 * @returns An array of an internal slices.
 */
function calculateAbsoluteGrouping(
  data: InternalSliceData[],
  threshold: ThresholdConfig,
  groupingName: string | undefined,
): InternalSliceData[] {
  const slicesWithValue = data.filter((slice) => slice.value);

  const [slicesToMerge, remainingSlices] = partition(
    slicesWithValue,
    (slice) => slice.value < threshold.value,
  );

  return generateGroup(slicesToMerge, remainingSlices, groupingName);
}

/**
 * Organize the data in different groups and recalculates colors
 * @param slicesToMerge - slices to merge into the Other category
 * @param data - Information received from the dataset already prepared with some metadata.
 * @param groupingName - Name of the group
 * @returns An array of an internal slices.
 */
function generateGroup(
  slicesToMerge: InternalSliceData[],
  data: InternalSliceData[],
  groupingName?: string,
): InternalSliceData[] {
  const isWithoutGrouping = !slicesToMerge.length || slicesToMerge.length === 1;

  if (isWithoutGrouping) {
    return [...data, ...slicesToMerge];
  }

  // Returns the numerical value of the id of the first slice that is going to be merge
  const groupIndexValue = slicesToMerge[0].id.split('-')[1];

  const groupId = groupingName
    ? groupingName + '-' + groupIndexValue
    : 'other-' + groupIndexValue;

  const mergedSlice = slicesToMerge.reduce(
    (acc, curr) => ({
      ...acc,
      ...curr,
      category: groupingName ?? 'Other',
      value: acc.value + curr.value,
      relativeValue: acc.relativeValue + curr.relativeValue,
      color: slicesToMerge[0].color,
      id: groupId,
    }),
    {
      value: 0,
      relativeValue: 0,
    } as InternalSliceData,
  );

  return [...data, mergedSlice];
}
